/**
 * The chat page -- the workbench channel's own surface.
 *
 * Saying something here is `ingress.submit`. What comes back was not delivered
 * to this page: since CHARTER §7.139 the core delivers nothing, and a reply is
 * a `tool_call` of `workbench.reply` that a `decision.fold` consumer put into
 * the decision. This shell owns that tool, so the words arrive as a call it
 * runs, and the bubble is folded out of the ledger rows the call left behind --
 * which is why reloading the page brings the same conversation back.
 *
 * Three bubbles, three sources: `message` is what the person said, `reply` is
 * what was said back and who asked for it, and `stream` is the `llm.chunk`
 * preview running ahead of the reply (§7.139 ②). A preview is not a record:
 * nothing writes it down, and the finished reply replaces it in place.
 *
 * Between them stand the calls a plugin's record panel claims, each drawn as
 * that plugin's own card at the point the call was made (`panel/records.tsx`).
 *
 * The Owner dock, expanded conversation and embedded workspace all use this
 * same conversation and composer.
 */

import { Accordion, Select, Button } from "../components/rumonai";
import { Fragment, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  api,
  ApiError,
  type ChatFrame,
  type ChatMessage,
  type ReplyOption,
  type SessionSummary,
  type TurnProgress,
  type PanelEntry,
  type ConversationOpening,
  type ApprovalCard,
  type ChatStopStatus,
} from "../lib/api";
import { formatClock, labelForState, toneForState } from "../lib/format";
import { AGENT_ID } from "../lib/base";
import { Avatar } from "../shell/Avatar";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../shell/ui";
import { ApprovalCardView } from "./ApprovalsPage";
import { AskCard } from "../studio/AskCard";
import { MarkdownText } from "../components/MarkdownText";
import { AttachmentCards } from "../components/attachments/AttachmentCards";
import { Icon } from "../components/Icon";
import { toolIndex, type ToolIndex } from "../studio/ask";
import { ChatStop } from "./ChatStop";
import { answeredOptions, optionAnswerLabels } from "./options";
import { Composer, sendMessage, type ComposerWorkView, type MessageOrigin } from "../shell/Composer";
import { useSettingsDirectory } from "../connections/context";
import { ModelReadiness } from "../connections/ModelReadiness";
import { declaredModelCheck } from "../connections/modelSettlement";
import type { SurfaceIntentDraft } from "../panel/SurfaceIntent";
import { drawnRecord, groupActivities, isUnderway, pendingRequestForRecord, RecordCard, RecordPanels, RecordRequestTrace, settledNotices, ToolActivity } from "../panel/records";
import { chatFeed } from "../lib/chatFeed";
import { pendingInteractionPanels } from "../shell/ownerShell";
import { ConversationViewport } from "../lib/conversationViewport";

/** A frame that tells a streamed bubble rather than naming a conversation to read. */
function tellsStream(frame: ChatFrame): boolean {
  return Boolean(frame.bubble) || typeof frame.grew === "string";
}

/** Several hand-offs can refer to one child; they remain separate history rows, but one piece of current work. */
function workIdentity(message: ChatMessage): string {
  const child = message.payload?.record?.read.child?.session_id;
  return child ? `child:${child}` : `record:${message.id}`;
}

interface RequestReading { element: HTMLElement; alignStart: boolean; position: number | null }
const CHAT_WINDOW = 200;
function recentWindow(messages: ChatMessage[]): ChatMessage[] {
  let start = Math.max(0, messages.length - CHAT_WINDOW);
  while (start > 0 && start < messages.length && messages[start].turn_id
    && messages[start].turn_id === messages[start - 1].turn_id) start--;
  return messages.slice(start);
}

/**
 * One telling of a streamed bubble, put on what this page holds.
 *
 * Which of two tellings is newer is their number (`stream_seq`), never the
 * length of their text: a preview can shrink when a reasoning block opens, and
 * a length is counted in different units here and on the server. A whole
 * bubble goes over the one with its id if it is newer, or after the last. A
 * growth is appended only onto the telling it was computed from, the one just
 * before it; anything else is a telling this page missed, and it reads the
 * conversation rather than guess (`gap`). `old` is a telling it already has,
 * or one a read already overtook. Never after a bubble of the same model
 * request: that is the reply that took the preview's place, so the preview is
 * `closed` -- nothing told of it later brings it back.
 */
function tell(messages: ChatMessage[], frame: ChatFrame, closed: Set<string>): ChatMessage[] | "old" | "gap" {
  const bubble = frame.bubble;
  const id = bubble ? bubble.id : frame.id ?? "";
  if (closed.has(id)) return "old";
  const at = messages.findIndex((message) => message.id === id);
  const held = at >= 0 ? messages[at].stream_seq ?? 0 : null;
  if (bubble) {
    if (held !== null) {
      return (bubble.stream_seq ?? 0) > held ? messages.map((message, index) => (index === at ? bubble : message)) : "old";
    }
    if (bubble.request_id && messages.some((message) => message.request_id === bubble.request_id)) {
      closed.add(id);
      return "old";
    }
    return [...messages, bubble];
  }
  const seq = frame.stream_seq ?? 0;
  if (held !== null && seq <= held) return "old";
  if (held === null || seq !== held + 1) return "gap";
  return messages.map((message, index) =>
    index === at ? { ...message, text: message.text + (frame.grew ?? ""), stream_seq: seq } : message);
}

export function ChatPage({
  sessionId,
  onPickSession,
  chatVersion,
  registryVersion = 0,
  onToast,
  connected,
  queueAvailable = false,
  ownerMode = false,
  title = "对话",
  dock = false,
  agentName = "创作助手",
  agentId = AGENT_ID,
  incomingSurfaceIntent,
  onSurfaceIntentPlaced,
  panels = [],
  origin,
  opening,
  aboveComposer,
  viewingWork,
  pendingApprovals = [],
}: {
  sessionId: string;
  opening?: string;
  /** Pending questions and presented pages remain in the conversation's scroll area. */
  aboveComposer?: ReactNode;
  /** The shell's existing subscription, including requests raised outside this conversation. */
  pendingApprovals?: ApprovalCard[];
  viewingWork?: ComposerWorkView;
  onPickSession: (id: string) => void;
  /** Moved by the shell when anything may have: every conversation reads again. */
  chatVersion: number;
  /** Moved when the registries did (`capabilities` frames): what the model check reads besides settings. */
  registryVersion?: number;
  onToast: (message: string, tone: "good" | "critical") => void;
  connected: boolean;
  queueAvailable?: boolean;
  ownerMode?: boolean;
  title?: string;
  dock?: boolean;
  agentName?: string;
  agentId?: string | null;
  incomingSurfaceIntent?: SurfaceIntentDraft | null;
  onSurfaceIntentPlaced?: (commandId: string) => void;
  panels?: PanelEntry[];
  origin?: MessageOrigin;
}) {
  const [messages, setMessages] = useState<ChatMessage[] | null>(null);
  const [progress, setProgress] = useState<TurnProgress | null>(null);
  /**
   * Work the Agent has going where this console does not speak -- a schedule's research, a chat channel's
   * film -- as the server lists it for the owner's conversation. No bubble of the log is it.
   */
  const [elsewhere, setElsewhere] = useState<ChatMessage[]>([]);
  /** The ones this page saw going on: they stay up, saying how they ended, until the owner next speaks. */
  const watched = useRef(new Set<string>());
  const [error, setError] = useState<unknown>(null);
  const [tools, setTools] = useState<ToolIndex>({});
  const [reload, setReload] = useState(0);
  const [sending, setSending] = useState(false);
  const choiceBusy = useRef(false);
  const [pendingChoices, setPendingChoices] = useState<Record<string, string>>({});
  const [savedOpening, setSavedOpening] = useState<{ session: string; value?: ConversationOpening; recorded: boolean }>();
  const currentOpening = savedOpening?.session === sessionId ? savedOpening.value : undefined;
  const recordedOpening = savedOpening?.session === sessionId && savedOpening.recorded && Boolean(currentOpening?.text);
  const [openingActions, setOpeningActions] = useState<HTMLDivElement | null>(null);
  const recordPanels = useContext(RecordPanels);
  /**
   * Which panels draw a card is decided when the conversation is read, so the
   * conversation is read again when they change: a panel registered after the
   * page loaded draws the calls made before it, and a withdrawn one stops.
   */
  const drawers = recordPanels.map((panel) => `${panel.key}@${panel.generation_id ?? ""}`).join(" ");
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;
  /**
   * The setting that reports whether this Agent can reach a model at all.
   *
   * Whichever settings page declared `readiness`, and no plugin's name: an
   * Agent whose owner has not connected a model yet can be talked to, and the
   * turn dies where nobody sees why -- the conversation said 「这次处理遇到
   * 问题」 and the model connection was never mentioned (r34's first walk).
   * `onlyWhenNeeded` means this draws nothing once a registered model is the
   * default, so an Agent that works has an unchanged conversation.
   */
  const modelCheck = declaredModelCheck(useSettingsDirectory());
  const [modelStatus, setModelStatus] = useState<"loading" | "ready" | "missing" | "unknown">("loading");
  const [atEnd, setAtEnd] = useState(true);
  const [stopStatus, setStopStatus] = useState<ChatStopStatus | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const olderBefore = useRef<string | null>(null);
  const olderLoading = useRef(false);
  const olderFailed = useRef(false);
  const loadedOlder = useRef(false);
  const historyEpoch = useRef(0);
  const restoreOlder = useRef<{ top: number; height: number } | null>(null);
  const [olderState, setOlderState] = useState<"idle" | "loading" | "failed">("idle");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Files dropped anywhere on the conversation go to the input bar.
  const attachRef = useRef<((files: File[]) => void) | null>(null);
  const [dropping, setDropping] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const workRef = useRef<HTMLDivElement>(null);
  const workButton = useRef<HTMLButtonElement>(null);
  const workId = useId();
  const responsesRef = useRef<HTMLDivElement>(null);
  const responsesId = useId();
  const [responsesVisible, setResponsesVisible] = useState(true);
  const [linkedApproval, setLinkedApproval] = useState(() => new URLSearchParams(window.location.hash.split("?")[1]).get("approval"));
  const focusedApproval = useRef<string | null>(null);
  const nearBottom = useRef(true);
  /**
   * The offset this page put the log at itself, or null since the reader moved.
   *
   * A `scroll` event does not say who scrolled, and this page scrolls the log on
   * purpose three times below. The event for one of those arrives after the
   * assignment, and a card that grew in between makes the distance to the end
   * look like a reader who had scrolled up: 「回到最新消息」 appeared on a page
   * already showing its last card, and stayed, because the observer that would
   * have re-pinned it only fires while `nearBottom` is still true. Which state a
   * run ended in depended on which scroll event came last -- measured on seven
   * rebuilds of the same conversation, offered in some and not in others, each
   * one stable once reached (D734).
   *
   * So the assignment is remembered, and an event that reports exactly it is
   * read as what it is: this page's own move, to the end.
   */
  const pinnedTop = useRef<number | null>(null);
  const readingRequest = useRef<RequestReading | null>(null);
  const openRequests = useRef(new Set<RequestReading>());

  /** A form may load after its heading: keep its start in view until the person starts reading or answering. */
  const showReadingRequest = useCallback(() => {
    const held = readingRequest.current;
    const log = logRef.current;
    if (!held || !log?.contains(held.element)) return false;
    if (held.alignStart) {
      log.scrollTop += held.element.getBoundingClientRect().top - log.getBoundingClientRect().top - log.clientTop;
      held.position = log.scrollTop;
    }
    setAtEnd(log.scrollHeight - log.scrollTop - log.clientHeight < 80);
    return true;
  }, []);

  const holdRequest = useCallback((element: HTMLElement) => {
    const held = { element, alignStart: true, position: null as number | null };
    openRequests.current.add(held);
    readingRequest.current = held;
    nearBottom.current = false;
    pinnedTop.current = null;
    showReadingRequest();
    // An old card can unmount after a new one opens; only release this card's hold.
    return () => {
      openRequests.current.delete(held);
      if (readingRequest.current === held) readingRequest.current = null;
    };
  }, [showReadingRequest]);

  const keepReadingPosition = () => {
    if (readingRequest.current) readingRequest.current.alignStart = false;
  };

  /** Put the log at its end, and remember that this page is what moved it. */
  const pinToEnd = useCallback(() => {
    const log = logRef.current;
    if (!log) return;
    log.scrollTop = log.scrollHeight;
    // Read back rather than reuse: the browser clamps to the scrollable range.
    pinnedTop.current = log.scrollTop;
  }, []);

  // The tool index names each call by the sentence its tool declared, in
  // either view. It is read again when the registries move and when the link
  // comes back; a read that fails keeps the names already known, and a call
  // left unnamed reads as a plain word, never as its key (`ToolActivity`).
  useEffect(() => {
    if (!connected) return;
    let live = true;
    api
      .capabilities()
      .then((body) => {
        if (live) setTools(toolIndex(body.registries));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [connected, registryVersion]);

  /**
   * What this page holds, kept beside the state so a frame is checked against
   * it the moment it arrives: frames come faster than renders, and a growth is
   * appended only onto the telling just before it (`tell`).
   */
  const held = useRef<ChatMessage[] | null>(null);
  const show = useCallback((next: ChatMessage[] | null) => {
    held.current = next;
    setMessages(next);
  }, []);
  const collapseReadHistory = useCallback(() => {
    const current = held.current;
    if (!loadedOlder.current || !current || current.length <= CHAT_WINDOW) return;
    const recent = recentWindow(current);
    if (recent.length === current.length) return;
    olderBefore.current = recent[0].id;
    loadedOlder.current = false;
    show(recent);
  }, [show]);
  const loadOlder = useCallback(() => {
    const before = olderBefore.current;
    if (!before || olderLoading.current) return;
    const epoch = historyEpoch.current;
    olderLoading.current = true;
    olderFailed.current = false;
    setOlderState("loading");
    void api.chat(sessionId, { limit: CHAT_WINDOW, before }).then((body) => {
      if (sessionRef.current !== sessionId || historyEpoch.current !== epoch) return;
      const current = held.current ?? [];
      const ids = new Set(current.map((message) => message.id));
      const prior = body.messages.filter((message) => !ids.has(message.id));
      if (prior.length) {
        const log = logRef.current;
        if (log) restoreOlder.current = { top: log.scrollTop, height: log.scrollHeight };
        show([...prior, ...current]);
      }
      olderBefore.current = body.next_before ?? null;
      loadedOlder.current = true;
    }).catch(() => {
      if (sessionRef.current !== sessionId || historyEpoch.current !== epoch) return;
      olderFailed.current = true;
      setOlderState("failed");
    }).finally(() => {
      if (sessionRef.current !== sessionId || historyEpoch.current !== epoch) return;
      olderLoading.current = false;
      if (!olderFailed.current) setOlderState("idle");
    });
  }, [sessionId, show]);
  /**
   * The read in flight, if one is: the streamed bubbles this page had heard of
   * when it was asked, and the tellings said since. The read may have been
   * answered before those were said, so they are told again over its answer
   * when it lands, and each goes on only if it is newer (`tell`).
   */
  const reading = useRef<{ heard: Set<string>; waiting: ChatFrame[] } | null>(null);
  /** Previews that ended: a read no longer has them, or a reply took their place. */
  const closed = useRef(new Set<string>());
  /** A telling this page missed: the conversation is read once more. */
  const [missed, setMissed] = useState(0);

  useEffect(() => {
    let live = true;
    const asked = {
      heard: new Set([
        ...(held.current ?? []).filter((message) => message.kind === "stream").map((message) => message.id),
        ...(reading.current?.waiting ?? []).map((frame) => frame.bubble?.id ?? frame.id ?? ""),
      ]),
      waiting: [] as ChatFrame[],
    };
    reading.current = asked;
    api
      .chat(sessionId, { limit: CHAT_WINDOW })
      .then((body) => {
        if (!live) return;
        if (reading.current === asked) reading.current = null;
        let next = body.messages;
        let line = body.progress;
        // A preview heard of before the read was asked, and not in its answer,
        // has ended since; a late telling of it does not bring it back.
        const standing = new Set(next.filter((message) => message.kind === "stream").map((message) => message.id));
        for (const id of asked.heard) if (!standing.has(id)) closed.current.add(id);
        let gap = false;
        for (const frame of asked.waiting) {
          const told = tell(next, frame, closed.current);
          if (told === "gap") gap = true;
          if (typeof told === "string") continue;
          next = told;
          if (frame.progress !== undefined) line = frame.progress;
        }
        if (loadedOlder.current && held.current?.length && next.length) {
          const overlap = held.current.findIndex((message) => message.id === next[0].id);
          if (overlap >= 0) next = [...held.current.slice(0, overlap), ...next];
          else loadedOlder.current = false;
        }
        if (!loadedOlder.current) olderBefore.current = body.next_before ?? null;
        show(next);
        setProgress(line);
        const away = body.elsewhere ?? [];
        for (const item of away) if (isUnderway(item.payload?.record?.read.state)) watched.current.add(item.id);
        setElsewhere(away);
        setSavedOpening({ session: sessionId, value: body.opening, recorded: body.opening_recorded === true });
        setError(null);
        if (gap) setMissed((n) => n + 1);
      })
      .catch((err) => {
        if (!live) return;
        if (reading.current === asked) reading.current = null;
        setError(err);
      });
    return () => {
      live = false;
    };
  }, [sessionId, chatVersion, reload, drawers, missed, show]);

  /**
   * The developer's session picker is the one place the session list is shown,
   * so it is read here, and only while the picker is: when the page opens, when
   * the shell says anything may have moved, and when a conversation did. The
   * Owner's conversation has no picker and reads no list.
   */
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [listed, setListed] = useState(0);
  useEffect(() => {
    if (ownerMode) return;
    let live = true;
    api
      .sessions()
      .then((body) => {
        if (live) setSessions(body.sessions);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [ownerMode, chatVersion, listed]);

  /**
   * What the stream says about conversations, heard here and not in the shell:
   * this page is the one that knows which conversation it shows. Another
   * conversation's news is not its news -- a card moving is said in the
   * conversation that drew it, and what a child says and does in the child's
   * own, heard by the open fold that shows it (`WorkbenchService.
   * _conversations_moved`, `ChildConversation` in `records.tsx`).
   * A streamed chunk's telling is put on the page and nothing is read, unless
   * the page missed the telling before it: then it reads once (`tell`).
   * Anything else about this conversation, or a frame naming none, is one read
   * of it.
   */
  useEffect(
    () =>
      chatFeed.subscribe((frame) => {
        if (tellsStream(frame)) {
          if (frame.session_id !== sessionId) return;
          // Told again over the answer of the read in flight, when there is one.
          reading.current?.waiting.push(frame);
          if (held.current === null) return;
          const told = tell(held.current, frame, closed.current);
          if (told === "gap") {
            // Read once, and hold what is said meanwhile for that read.
            if (!reading.current) {
              reading.current = { heard: new Set(), waiting: [] };
              setMissed((n) => n + 1);
            }
            return;
          }
          if (told === "old") return;
          show(told);
          if (frame.progress !== undefined) setProgress(frame.progress);
          return;
        }
        if (!ownerMode) setListed((n) => n + 1);
        if (frame.session_id === null || frame.session_id === sessionId) setReload((n) => n + 1);
      }),
    [sessionId, ownerMode, show],
  );

  useLayoutEffect(() => {
    show(null);
    reading.current = null;
    closed.current = new Set();
    watched.current = new Set();
    setElsewhere([]);
    setProgress(null);
    setStopStatus(null);
    setSavedOpening(undefined);
    setPendingChoices({});
    setWorkOpen(false);
    historyEpoch.current++;
    olderBefore.current = null;
    olderLoading.current = false;
    olderFailed.current = false;
    loadedOlder.current = false;
    restoreOlder.current = null;
    setOlderState("idle");
    if (readingRequest.current && !logRef.current?.contains(readingRequest.current.element)) readingRequest.current = null;
    nearBottom.current = !readingRequest.current;
    setAtEnd(!readingRequest.current);
  }, [sessionId, show]);

  useLayoutEffect(() => {
    const restore = restoreOlder.current;
    if (restore && logRef.current) {
      restoreOlder.current = null;
      logRef.current.scrollTop = restore.top + logRef.current.scrollHeight - restore.height;
      nearBottom.current = false;
      return;
    }
    // Follow new text only while the reader is already at the end. Scroll
    // this conversation, never the document or another Agent's work surface.
    if (!showReadingRequest() && nearBottom.current) pinToEnd();
  }, [messages, pinToEnd, showReadingRequest]);

  const follow = useRef<ResizeObserver | null>(null);
  useEffect(() => {
    const log = logRef.current;
    if (!log || typeof ResizeObserver === "undefined") return;
    // An embedded conversation keeps its message state while it has no room.
    // When it gets space again, follow the latest text only if the reader had
    // already been following it.
    const resize = new ResizeObserver(() => {
      if (log.clientHeight > 0 && !showReadingRequest() && nearBottom.current) pinToEnd();
    });
    resize.observe(log);
    follow.current = resize;
    return () => {
      follow.current = null;
      resize.disconnect();
    };
  }, [pinToEnd, showReadingRequest]);

  /**
   * Bubbles that fill themselves in after they are drawn.
   *
   * A question card reads the tool it is about and grows; an attachment loads
   * and grows; a disclosure opens. None of that changes the log's own box, so
   * the observer above never hears about it -- and the reader who opened the
   * conversation at its end is left a screen and a half above it, looking at
   * the middle of something with 「回到最新消息」 offered as if they had
   * scrolled there themselves. Measured on the Owner's 对话 page: 1451 pixels
   * below the end on a conversation with two question cards in it.
   */
  useLayoutEffect(() => {
    const log = logRef.current;
    if (!log || !follow.current) return;
    for (const child of Array.from(log.children)) follow.current.observe(child);
  }, [messages, aboveComposer, pendingApprovals]);

  useEffect(() => {
    const changed = () => {
      focusedApproval.current = null;
      setLinkedApproval(new URLSearchParams(window.location.hash.split("?")[1]).get("approval"));
    };
    window.addEventListener("hashchange", changed);
    return () => window.removeEventListener("hashchange", changed);
  }, []);

  useEffect(() => {
    if (!linkedApproval || focusedApproval.current === linkedApproval) return;
    const card = Array.from(responsesRef.current?.querySelectorAll<HTMLElement>("[data-approval-key]") ?? [])
      .find(node => node.dataset.approvalKey === linkedApproval);
    if (!card) return;
    nearBottom.current = false;
    pinnedTop.current = null;
    card.scrollIntoView?.({ block: "nearest" });
    card.focus({ preventScroll: true });
    focusedApproval.current = linkedApproval;
  }, [linkedApproval, pendingApprovals]);

  useEffect(() => {
    if (!workOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      setWorkOpen(false);
      workButton.current?.focus({ preventScroll: true });
    };
    const outside = (event: PointerEvent) => {
      // Confirmation dialogs and option menus render in portals. A press in one still belongs to its work card.
      if (event.target instanceof Node && workRef.current?.closest(".chat")?.contains(event.target)
        && !workRef.current.contains(event.target)) setWorkOpen(false);
    };
    document.addEventListener("keydown", close);
    document.addEventListener("pointerdown", outside);
    return () => { document.removeEventListener("keydown", close); document.removeEventListener("pointerdown", outside); };
  }, [workOpen]);

  const jumpToLatest = () => {
    readingRequest.current = null;
    nearBottom.current = true;
    setAtEnd(true);
    collapseReadHistory();
    pinToEnd();
  };

  /**
   * A button on a reply, pressed.  CHARTER §7.135: the road typing it takes.
   *
   * The composer's road is `Composer`, above the page now.  This one stays here
   * because the buttons are part of the bubble, and because an option carries
   * neither a draft nor a page request: it is one value, already written down in
   * the question it answers.
   */
  const answer = async (text: string, replyId: string) => {
    if (!text || choiceBusy.current || pendingChoices[replyId] || !connected) return;
    choiceBusy.current = true;
    jumpToLatest();
    setSending(true);
    try {
      await sendMessage(sessionId, text, origin, replyId);
      watched.current.clear();
      if (sessionRef.current === sessionId) {
        setPendingChoices(current => ({ ...current, [replyId]: text }));
        setReload((n) => n + 1);
      }
    } catch (err) {
      if (sessionRef.current !== sessionId) return;
      onToast(`发送失败：${ApiError.fromUnknown(err).detail}`, "critical");
    } finally {
      choiceBusy.current = false;
      setSending(false);
    }
  };

  const answeredChoices = answeredOptions(messages ?? []);
  const choiceLabels = optionAnswerLabels(messages ?? []);
  const latestInputAt = messages?.reduce((latest, message) =>
    message.role === "user" && message.kind === "message" ? Math.max(latest, Number(message.ts) || 0) : latest, 0) ?? null;
  const cards = (messages ?? []).flatMap((message) => {
    const card = drawnRecord(message, recordPanels);
    return card ? [{ id: message.id, turn: message.turn_id, card, message }] : [];
  });
  const pendingPanels = ownerMode && aboveComposer ? pendingInteractionPanels(panels) : [];
  const questionTraces = new Map((messages ?? []).flatMap(message => {
    const panel = pendingRequestForRecord(message, pendingPanels);
    return panel ? [[message.id, panel] as const] : [];
  }));
  const traceIds = new Set(questionTraces.keys());
  // A turn that drew a card said something back, whatever happened after it
  // (`chat.py` `settle_input` leaves this to the page, which knows what it drew).
  const cardTurns = new Set([...cards.map((item) => item.turn),
    ...(messages ?? []).filter(message => traceIds.has(message.id)).map(message => message.turn_id)].filter(Boolean));
  /**
   * Work that outlived the turn that started it: a child still working, a call
   * still running in the background. Its card is up where the call was made
   * and a long conversation buries it, so its current read opens beside the
   * composer without scrolling back to the original hand-off. The turn in progress is left out: its
   * cards are at the end of the log already.
   */
  const underway = [...new Map(cards.filter((item) => isUnderway(item.card.read.state)
    && !questionTraces.has(item.message.id)
    && (!progress || item.turn !== progress.turn_id)).map(item => [workIdentity(item.message), item])).values()];
  const here = new Set(underway.map(item => workIdentity(item.message)));
  // Work that started elsewhere: all that is going on, and what this page watched until the owner speaks again.
  const away = ownerMode ? [...new Map(elsewhere.filter((item) => drawnRecord(item, recordPanels)
    && !pendingRequestForRecord(item, pendingPanels)
    && !here.has(workIdentity(item)) && (watched.current.has(item.id) || isUnderway(item.payload?.record?.read.state)))
    .map(item => [workIdentity(item), item])).values()] : [];
  const activeWork = underway.length + away.filter(item => isUnderway(item.payload?.record?.read.state)).length;
  const workPreview = underway[0]?.card.line ?? (away.length ? drawnRecord(away[0], recordPanels)?.line : null);
  const approvals = ownerMode ? [...new Map(pendingApprovals.filter(card => !card.resolved).map(card => [card.key, card])).values()] : [];
  const approvalKeys = new Set(approvals.map(card => card.key));
  const pendingRequests = pendingPanels.length;
  const responseCount = approvals.length + pendingRequests;
  useEffect(() => {
    const region = responsesRef.current;
    const log = logRef.current;
    if (!responseCount || !region || !log) return;
    if (typeof IntersectionObserver === "undefined") {
      setResponsesVisible(atEnd);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]) setResponsesVisible(entries[0].isIntersecting);
    }, { root: log, threshold: 0 });
    observer.observe(region);
    return () => observer.disconnect();
  }, [responseCount, atEnd]);
  const showResponses = () => {
    nearBottom.current = false;
    pinnedTop.current = null;
    responsesRef.current?.scrollIntoView?.({ block: "start" });
    responsesRef.current?.focus({ preventScroll: true });
  };

  return (
    <div
      id={dock ? "agent-chat-content" : undefined}
      className={`chat${dock ? " chat-dock" : ownerMode ? " chat-own" : ""}`}
      data-dropping={dropping || undefined}
      onDragOver={(e) => {
        if (!Array.from(e.dataTransfer.types).includes("Files") || !attachRef.current) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setDropping(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false);
      }}
      onDrop={(e) => {
        setDropping(false);
        const files = Array.from(e.dataTransfer.files);
        if (!files.length || !attachRef.current) return;
        e.preventDefault();
        attachRef.current(files);
      }}
    >
      <header className="page-head">
        {dock ? (
          <h2 className="page-title">{title}</h2>
        ) : (
          <h1 className="page-title" tabIndex={-1}>
            {title}
          </h1>
        )}
        {!ownerMode ? (
          <div className="session-picker">
            <label className="micro" htmlFor="session-select">
              会话
            </label>
            <Select id="session-select" label="会话" value={sessionId} onValueChange={onPickSession}
              items={(sessions.some(s => s.id === sessionId) ? sessions : [{ id: sessionId, message_count: 0 }, ...sessions])
                .map(s => ({ value: s.id, label: `${s.id}（${s.message_count} 条）` }))} />
          </div>
        ) : null}
      </header>

      <div className="chat-stage">
      <div
        className="chat-log"
        data-testid="chat-log"
        ref={logRef}
        onWheelCapture={keepReadingPosition}
        onTouchMoveCapture={keepReadingPosition}
        onPointerDownCapture={keepReadingPosition}
        onKeyDownCapture={keepReadingPosition}
        onScroll={() => {
          const log = logRef.current;
          if (!log || log.clientHeight <= 0) return;
          const held = readingRequest.current;
          if (held && log.contains(held.element)) {
            // A short loading card can temporarily be at the end. That geometry
            // does not change the person's explicit intent to read the question.
            if (held.position !== null && log.scrollTop !== held.position) held.alignStart = false;
            held.position = log.scrollTop;
            nearBottom.current = false;
            pinnedTop.current = null;
            setAtEnd(log.scrollHeight - log.scrollTop - log.clientHeight < 80);
            return;
          }
          if (pinnedTop.current !== null && log.scrollTop === pinnedTop.current) {
            // This page's own move, echoed back. It put the log at the end, so
            // that is the answer -- whatever the arithmetic says now that the
            // cards below have grown (D734). The observer above re-pins to the
            // new end, which is only reached while this stays true.
            nearBottom.current = true;
            setAtEnd(true);
            return;
          }
          pinnedTop.current = null;
          nearBottom.current = log.scrollHeight - log.scrollTop - log.clientHeight < 80;
          setAtEnd(nearBottom.current);
          if (nearBottom.current) collapseReadHistory();
          if (log.scrollTop < 72 && olderBefore.current && !olderFailed.current && !readingRequest.current) loadOlder();
        }}
      >
        {olderState === "loading" ? <div className="chat-history-notice" role="status">正在读取更早消息…</div> : null}
        {olderState === "failed" ? <button className="chat-history-notice" type="button" onClick={loadOlder}>更早消息读取失败，重试</button> : null}
        {error ? <ErrorState error={error} onRetry={() => setReload((n) => n + 1)} /> : null}
        {ownerMode && messages !== null && (messages.length === 0 || recordedOpening) ? <div className="chat-opening">
          <Avatar name={agentName} identity={agentId} className="bubble-avatar is-agent" />
          <div><p>{currentOpening?.text || opening || "你想从什么开始？"}</p>
            <div ref={setOpeningActions} /></div></div> : null}
        {messages === null && !error ? (
          <LoadingState label="读会话" />
        ) : messages?.length === 0 ? (
          ownerMode ? null : <EmptyState title="这条会话还没有内容" hint="在下面说一句话，Agent 会在这里回应。" />
        ) : (
          groupActivities(settledNotices(messages ?? [], recordPanels).filter(message =>
            !message.payload?.approval || !approvalKeys.has(message.payload.approval.key)), traceIds).map((message) => Array.isArray(message)
            ? <ToolActivity key={message[0].id} messages={message} tools={tools} ownerMode={ownerMode} />
            : questionTraces.has(message.id) ? <RecordRequestTrace key={message.id} message={message} panel={questionTraces.get(message.id)!} /> : (
            <Bubble
              key={message.role === "agent" && message.request_id ? `response:${message.request_id}` : message.id}
              message={message}
              choiceLabel={choiceLabels[message.id]}
              agentName={agentName}
              agentId={agentId}
              onAnswer={(value) => void answer(value, message.id)}
              answer={answeredChoices[message.id] ?? pendingChoices[message.id] ?? null}
              busy={sending || !connected}
              onToast={onToast}
              ownerMode={ownerMode}
              tools={tools}
              interactiveRecords={connected}
              version={chatVersion + reload}
              onDone={() => setReload((n) => n + 1)}
              turnDrewCard={!!message.turn_id && cardTurns.has(message.turn_id)}
              stopStatus={stopStatus?.terminal && stopStatus.turn_id === message.turn_id ? stopStatus : null}
            />
          ))
        )}
        <div ref={responsesRef} id={responsesId} className="chat-responses" tabIndex={-1} role={responseCount ? "region" : undefined}
          aria-label={responseCount ? "待回应事项" : undefined}
          onFocusCapture={(event) => {
            nearBottom.current = false;
            pinnedTop.current = null;
            // After explicitly following the latest messages, returning to an
            // open question keeps the field in view without reopening the card.
            const request = Array.from(openRequests.current).reverse().find(held => held.element.contains(event.target));
            if (request && readingRequest.current !== request) {
              request.alignStart = false;
              request.position = logRef.current?.scrollTop ?? null;
              readingRequest.current = request;
            }
          }}>
          <ConversationViewport.Provider value={holdRequest}>
          {aboveComposer}
          {approvals.length ? <section className="chat-approvals" aria-label="需要你确认">
            <h2 className="chat-requests-heading"><Icon name="question" size={16} />需要你确认<span>{approvals.length}</span></h2>
            {approvals.map(card => <div key={card.key} data-approval-key={card.key} tabIndex={-1}>
              <AskCard card={card} tools={tools} onToast={onToast} disabled={!connected}
                onDone={() => setReload(n => n + 1)} />
            </div>)}
          </section> : null}
          </ConversationViewport.Provider>
        </div>
      {!atEnd ? <button type="button" className="chat-jump" aria-label="回到最新消息" title="回到最新消息" onClick={jumpToLatest}>
        <Icon name="down" size={16} />
      </button> : null}
      </div>

      </div>

      <div className="chat-activity">
      {underway.length || away.length ? <div ref={workRef} className="chat-work">
        <button ref={workButton} className="btn btn-quiet chat-work-toggle" type="button" onClick={() => setWorkOpen(value => !value)}
          aria-label={`${workOpen ? "收起" : "展开"}后台工作，${activeWork ? `${activeWork} 项进行中` : "有新结果"}`}
          aria-expanded={workOpen} aria-controls={workId}>
          <span className="chat-activity-dot" data-state={activeWork ? "working" : "done"} aria-hidden="true" />
          <span className="chat-work-label"><strong>{activeWork ? `后台仍有 ${activeWork} 项工作` : "后台工作有新结果"}</strong>
            {workPreview ? <small data-quoted="true">{workPreview}</small> : null}</span>
          <Icon name={workOpen ? "up" : "down"} size={14} />
        </button>
        {workOpen ? <section id={workId} className="chat-work-details" aria-label="后台工作记录">
          {underway.map(item => <Bubble key={item.id} message={item.message} agentName={agentName} agentId={agentId}
            ownerMode={ownerMode} tools={tools} interactiveRecords={connected} version={chatVersion + reload} openThread />)}
          {away.length ? <div className="chat-elsewhere" role="group" aria-label="别处在做的工作">
            {away.map(item => <Bubble key={item.id} message={item} agentName={agentName} agentId={agentId} ownerMode={ownerMode}
              tools={tools} interactiveRecords={connected} version={chatVersion + reload} openThread />)}
          </div> : null}
        </section> : null}
      </div> : null}
      {responseCount && !responsesVisible ? <button className="btn btn-quiet chat-response-entry" type="button"
        aria-controls={responsesId} onClick={showResponses}>
        <Icon name="question" size={16} />等你回应 · {responseCount}
      </button> : null}
      {progress ? (
        <div className="turn-progress" role="status" aria-live="polite">
          <span className="chat-activity-dot" data-state="working" aria-hidden="true" />
          {/* The owner is told whether the message was picked up, and nothing
              about how: which call is running is a card's to say, and only a
              card whose plugin claimed that call says it. */}
          <StatusBadge
            tone="neutral"
            label={ownerMode ? progress.state === "received" ? "消息已收到，等待开始" : "正在处理这条消息" : `第 ${progress.round} 轮`}
          />
          {!ownerMode ? (
            <span className="micro">
              欠条 <code className="mono">{progress.obligation_id}</code>
              {progress.tool ? (
                <>
                  {" "}
                  · 正在调 <code className="mono">{progress.tool}</code>
                </>
              ) : (
                " · 还没有进工具轮"
              )}
            </span>
          ) : null}
        </div>
      ) : null}

      <ChatStop key={sessionId} sessionId={sessionId} progress={progress} latestInputAt={latestInputAt} version={chatVersion + reload} connected={connected}
        hasBackgroundWork={activeWork > 0} onStatus={setStopStatus} />
      </div>

        {modelCheck ? (
          <ModelReadiness
            onlyWhenNeeded
            active={connected}
            sessionId={sessionId}
            version={registryVersion}
            check={modelCheck}
            onStatus={setModelStatus}
          />
        ) : null}

        <Composer
          suggestions={ownerMode && !error && messages?.length === 0 ? currentOpening?.options : undefined}
          suggestionsTarget={openingActions}
          agentName={agentName}
          viewingWork={viewingWork}
          sessionId={sessionId}
          connected={connected}
          queueAvailable={queueAvailable}
          origin={origin}
          panels={panels}
          incomingSurfaceIntent={incomingSurfaceIntent}
          onSurfaceIntentPlaced={onSurfaceIntentPlaced}
          onToast={onToast}
          onSent={() => {
            watched.current.clear();
            jumpToLatest();
            setReload((n) => n + 1);
          }}
          inputRef={inputRef}
          attachRef={attachRef}
          compact={dock || ownerMode}
          sendDisabled={Boolean(modelCheck && modelStatus === "missing")}
        />
    </div>
  );
}

/**
 * One bubble.  Exported because a child's conversation, opened under a card, is
 * read back through the same route and drawn with the same bubbles -- a second
 * renderer for it would be a second set of answers to what a message looks like.
 *
 * Everything but the message itself has a default, so the child's reading only
 * says the two things that differ there: who is talking, and who was asked.
 */
export function Bubble({
  message,
  choiceLabel,
  agentName,
  agentId,
  /** Who the `user` side is. The Agent, in a subagent's own record: the work
      was handed over by it and not typed by anybody. */
  userName = "你",
  onAnswer = () => {},
  answer = null,
  busy = false,
  onToast = () => {},
  ownerMode,
  tools = {},
  version = 0,
  onDone = () => {},
  turnDrewCard = false,
  stopStatus = null,
  interactiveRecords = false,
  openThread = false,
}: {
  message: ChatMessage;
  choiceLabel?: string;
  agentName: string;
  agentId?: string | null;
  userName?: string;
  onAnswer?: (value: string) => void;
  /** The option this question was already answered with, if it was. */
  answer?: string | null;
  busy?: boolean;
  onToast?: (text: string, tone: "good" | "critical") => void;
  ownerMode?: boolean;
  tools?: ToolIndex;
  /** Bumped by the one stream; what re-reads an opened child's conversation. */
  version?: number;
  onDone?: () => void;
  /** The turn this message started drew a card, so it was answered. */
  turnDrewCard?: boolean;
  stopStatus?: ChatStopStatus | null;
  interactiveRecords?: boolean;
  /** The current-work disclosure reads the child's latest conversation immediately. */
  openThread?: boolean;
}) {
  const [explanation, setExplanation] = useState<string[]>([]);
  const textId = `message-body-${message.id}`;
  const stoppedHere = message.role === "user" && stopStatus?.turn_id === message.turn_id && stopStatus.terminal;
  // A call a plugin claims, drawn as that plugin's card where it was made. Like
  // the approval card below, this is a reading of the record made on the way
  // out and never a second thing the conversation holds. The child's own
  // conversation under it is drawn with these bubbles: the Agent handed the
  // work over, so it is the one the child's messages answer.
  if (message.kind === "record") {
    return (
      <RecordCard
        message={message}
        version={version}
        onToast={interactiveRecords ? onToast : undefined}
        openThread={openThread}
        said={(said, name) =>
          groupActivities(said).map((item) => Array.isArray(item)
            ? <ToolActivity key={item[0].id} messages={item} tools={tools} ownerMode={ownerMode} /> : (
            <Bubble key={item.id} message={item} agentName={name} userName={agentName} ownerMode={ownerMode} tools={tools} version={version} />
          ))
        }
      />
    );
  }
  // An approval card is a reply this shell managed to put beside an effect the
  // core is holding. The reply underneath is an ordinary one; the card is the
  // reading, and it is drawn here rather than delivered (§7.139 ⑤).
  if (message.payload?.approval) {
    return (
      <article className="bubble bubble-approval" data-role="approval">
        {ownerMode ? (
          <AskCard
            card={message.payload.approval}
            tools={tools}
            onToast={onToast}
            onDone={onDone}
            disabled={busy}
          />
        ) : (
          <ApprovalCardView
            card={message.payload.approval}
            compact
            onToast={onToast}
          />
        )}
      </article>
    );
  }
  const streaming = message.streaming;
  return (
    <article
      className={`bubble bubble-${message.role}`}
      data-role={message.role}
      data-kind={message.kind}
      data-testid={`bubble-${message.kind}`}
    >
      <Avatar name={message.role === "user" ? userName : agentName}
        identity={message.role === "agent" ? agentId : undefined}
        glyph={message.role === "user" ? "person" : "agent"}
        className={`bubble-avatar${message.role === "agent" ? " is-agent" : ""}`} />
      <header className="bubble-head micro">
        {/* In the Owner's own conversation what they said is on the right and needs no name; the Agent's is above its words. */}
        <span className={ownerMode && message.role === "user" ? "visually-hidden" : "bubble-name"}>{message.role === "user" ? userName : agentName}</span>
        <time>{formatClock(message.ts)}</time>
        {streaming ? <span className="streaming">还在说</span> : null}
        {message.kind === "reply" && !ownerMode ? (
          <ReplyMark message={message} />
        ) : null}
      </header>
      {message.role === "agent" ? (
        // A reply that is only the film has no empty line above it.
        message.text || streaming || !message.attachments?.length ? <div id={textId} className="bubble-text bubble-markdown" data-quoted="true">
          <MarkdownText text={message.text} breaks />
          {streaming ? <span className="caret" aria-hidden="true" /> : null}
        </div> : null
      ) : choiceLabel || message.text || !message.attachments?.length
        // Files given without a word are the message; there is no empty line above them.
        ? <p id={textId} className="bubble-text" data-quoted="true">{choiceLabel ? `已选择：${choiceLabel}` : message.text}</p>
        : null}
      {message.explanation ? <div className="bubble-explanation"><Accordion value={explanation} onValueChange={setExplanation}
        items={[{ value: "explanation", title: "完整说明", content: <MarkdownText text={message.explanation} breaks /> }]} /></div> : null}
      {/* What was produced, under what was said about it. A file is not a
          sentence: the text may be 「成片在这里」 and the film itself is the
          thing the reader came for (§the Owner's reference draws every
          artifact as a card). */}
      <AttachmentCards items={message.attachments} />
      {message.role === "user" && message.state === "unanswered" && !stoppedHere ? (
        // The turn this message started failed before saying anything back --
        // unless it drew a card, which is saying something.
        turnDrewCard ? null : (
          <p className="bubble-error micro" role="alert">
            {ownerMode
              // A plugin that ended it on purpose wrote why for the person; anything else is not theirs to read.
              ? message.notice ? `这条没能回复：${message.notice}` : "这条没能回复，可以再发一次。"
              : message.error ? `这条没能回复：${message.error}` : "这条没能回复，可以再发一次。"}
          </p>
        )
      ) : message.error ? (
        <p className="bubble-error micro" role="alert">
          {ownerMode ? "这条回复暂时未能送达。" : `这句话没送出去：${message.error}`}
        </p>
      ) : null}
      {stoppedHere ? <p className="bubble-stop micro" role="status">
        {stopStatus?.outcome === "stopped" ? "已停止回复" : "这条回复已结束，停止结果未确认"}
      </p> : null}
      {message.options.length ? (
        <Options options={message.options} onAnswer={onAnswer} answer={answer} busy={busy} />
      ) : null}
    </article>
  );
}

/**
 * Who asked for this reply, and whether it left the building.
 *
 * `author` is the `source` column of the `tool_call` row: the fold consumer's
 * plugin name when one put the call there, `model` or `preset` otherwise
 * (§7.139 ①). It is the fact the old delivery row could not state -- a message
 * the model never asked for looked exactly like one it did -- so it is shown
 * rather than kept for the ledger page.
 */
function ReplyMark({ message }: { message: ChatMessage }) {
  const state = message.state ?? "sending";
  return (
    <>
      {message.author ? (
        <span
          className="bubble-author"
          data-testid="reply-author"
          title="是谁把这次回复放进决定里的"
        >
          {authorLabel(message.author)}
        </span>
      ) : null}
      <StatusBadge tone={toneForState(state)} label={labelForState(state)} />
    </>
  );
}

function authorLabel(author: string): string {
  if (author === "model") return "模型自己说的";
  if (author === "preset") return "按钮带来的";
  return `${author} 折进来的`;
}

/**
 * CHARTER §7.139 ⑤: buttons are a plain reply with choices on it.
 *
 * Pressing one submits its value as an ordinary input -- the same road typing
 * it would take (§7.135). This component knows nothing about approvals; a
 * reply that happens to be an approval question is drawn as a card instead,
 * higher up, and that reading is made from the ledger and not from here.
 */
function Options({
  options,
  onAnswer,
  answer,
  busy,
}: {
  options: ReplyOption[];
  onAnswer: (value: string) => void;
  answer: string | null;
  busy: boolean;
}) {
  // An answered question keeps its buttons -- what was offered is part of
  // reading what was chosen -- and none of them do anything any more: the
  // answer already went, and pressing one again would send it to an agent
  // that is somewhere else now (D305).
  return (
    <div className="bubble-options" data-testid="reply-options" data-answered={answer === null ? undefined : ""}>
      {options.map((option, index) => <Fragment key={option.value}>
        {index > 0 ? <span className="bubble-options-divider" aria-hidden="true" /> : null}
        <Button
          variant={index === options.findIndex(item => item.approves) ? "primary" : "secondary"}
          // `approves` is the one thing an option says about itself: the
          // option that goes ahead (the permission plugin's options). The
          // rest are ordinary choices and are drawn as such -- a `tone` was
          // read here for a while and no plugin has ever written one.
          className={option.value === answer ? "is-chosen" : ""}
          disabled={busy || answer !== null}
          onClick={() => onAnswer(option.value)}
        >
          {option.label}
          {option.value === answer ? <span className="micro">已选择</span> : <Icon name="arrow" size={15} />}
        </Button>
      </Fragment>)}
    </div>
  );
}
